import { AlertTriangle, ArrowLeft, ChevronRight, Lock, RotateCw, WifiOff } from 'lucide-react';
import { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { adminCopy } from '@/lib/copy';
import { cn } from '@/lib/utils';

export interface ErrorStateProps {
  /** When set, overrides the default title. */
  title?: string;
  /** When set, overrides the default description. May be a raw backend
   *  message — the component sniffs for known noisy strings and falls back
   *  to friendlier copy while preserving the technical detail in a
   *  collapsible <details> disclosure. */
  description?: string;
  /** True = render forbidden copy, no retry button. */
  forbidden?: boolean;
  onRetry?: () => void;
  /** Optional secondary action — e.g. "Go back" via `router.history.back()`. */
  secondaryAction?: { label: string; onClick: () => void };
  /** Short status/code label rendered as a small eyebrow chip top-right
   *  (e.g. "HTTP 500", "NETWORK", "FORBIDDEN"). Hidden when omitted. */
  code?: string;
  className?: string;
}

// Backend stack-trace and generic-noise patterns that aren't useful to a
// human — we keep them in a collapsible disclosure but show a friendlier
// description above. Order matters: more-specific first.
const NOISY_DESCRIPTION_PATTERNS: RegExp[] = [
  /^An unexpected error occurred\.?$/i,
  /^Failed query:/i,
  /^Request failed with status code/i,
  /^HTTP \d{3}$/i,
  /^Network ?Error$/i,
];

function isNoisyDescription(text: string): boolean {
  return NOISY_DESCRIPTION_PATTERNS.some((re) => re.test(text.trim()));
}

/**
 * Inline failure state — visual sibling of `EmptyState`. Three intent-aware
 * treatments, picked from props:
 *   - `forbidden`: lock chip + neutral tones, no retry. Copy points the user
 *     at their administrator.
 *   - `code === 'NETWORK'` (case-insensitive): wifi-off icon, warning amber —
 *     reads as a transient connectivity issue, not a server crash.
 *   - default: triangle icon + warm error wash, primary "Try again" button
 *     when `onRetry` is provided.
 *
 * Treatment mirrors the EmptyState recipe (rounded card, font-display title,
 * atmospheric backdrop) so failure surfaces don't feel like a different
 * design language from "empty" or "loading".
 */
export function ErrorState({
  title,
  description,
  forbidden,
  onRetry,
  secondaryAction,
  code,
  className,
}: ErrorStateProps) {
  const variant = forbidden ? 'forbidden' : code && /^network$/i.test(code) ? 'network' : 'error';

  const resolvedTitle =
    title ??
    {
      forbidden: 'Access restricted',
      network: "You're offline",
      error: 'Something went wrong',
    }[variant];

  // If the caller's description is a known-noisy backend string, hide it
  // behind a disclosure and lead with friendlier copy instead. Keeps the
  // technical detail one click away for diagnosis without making it the
  // first thing a user reads.
  const { primaryDescription, technicalDetail } = useMemo(() => {
    const fallbackByVariant: Record<typeof variant, string> = {
      forbidden: adminCopy.error.forbidden,
      network: 'Check your connection and try again — your work is safe.',
      error:
        "We couldn't load this just now. Try again — if it keeps happening, your team has been notified.",
    };

    if (!description) {
      return { primaryDescription: fallbackByVariant[variant], technicalDetail: undefined };
    }
    if (isNoisyDescription(description)) {
      return { primaryDescription: fallbackByVariant[variant], technicalDetail: description };
    }
    return { primaryDescription: description, technicalDetail: undefined };
  }, [description, variant]);

  const Icon = variant === 'forbidden' ? Lock : variant === 'network' ? WifiOff : AlertTriangle;

  // Tone palettes — every class below resolves against a real token in
  // globals.css. Earlier draft used `error-100/600` / `warning-100/200`
  // which silently fall back to default text color — that's what made
  // the icon stroke look black in the previous render.
  const tone = {
    forbidden: {
      iconBg: 'bg-neutral-100',
      iconText: 'text-neutral-700',
      halo: 'bg-neutral-200/60',
      eyebrow: 'text-neutral-500',
      eyebrowDot: 'bg-neutral-400',
      backdrop: 'bg-[radial-gradient(ellipse_at_center,rgba(3,68,92,0.06)_0%,transparent_60%)]',
    },
    network: {
      iconBg: 'bg-warning-50',
      iconText: 'text-warning-700',
      halo: 'bg-warning-300/35',
      eyebrow: 'text-warning-700',
      eyebrowDot: 'bg-warning-500',
      backdrop: 'bg-[radial-gradient(ellipse_at_center,rgba(217,119,6,0.08)_0%,transparent_60%)]',
    },
    error: {
      iconBg: 'bg-error-50',
      iconText: 'text-error-500',
      halo: 'bg-error-300/35',
      eyebrow: 'text-error-700',
      eyebrowDot: 'bg-error-500',
      backdrop: 'bg-[radial-gradient(ellipse_at_center,rgba(239,68,68,0.07)_0%,transparent_55%)]',
    },
  }[variant];

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        // Tightened proportions — narrower (max-w-md) and shorter (py-9)
        // so the state feels composed, not like an oversized modal pasted
        // into the page. Padding is the same horizontally as a Button
        // group needs to breathe.
        'bg-surface relative mx-auto flex w-full max-w-md flex-col items-center gap-4 overflow-hidden rounded-2xl border border-(--color-border) px-6 py-9 text-center shadow-xs',
        className,
      )}
    >
      {/* Single soft tonal radial behind the content. The previous version
          layered a diagonal grid on top — it added noise without adding
          meaning, especially at the smaller card size. */}
      <span aria-hidden className={cn('pointer-events-none absolute inset-0', tone.backdrop)} />

      {/* Optional status/code chip — top-right anchor for diagnosis. The dot
          uses a saturated tone token so it reads as a status pip even at
          12px, while the eyebrow text stays softer. */}
      {code ? (
        <span
          className={cn(
            'text-meta-sm tracking-eyebrow bg-surface absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full border border-(--color-border) px-2.5 py-1 font-semibold uppercase tabular-nums',
            tone.eyebrow,
          )}
        >
          <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', tone.eyebrowDot)} />
          {code}
        </span>
      ) : null}

      {/* Icon stack — solid tonal chip with a soft halo behind it. No ring
          this time: the prior ring used a non-existent token and rendered
          as a hard near-black edge. The halo alone gives the chip presence
          without an artificial border. */}
      <span aria-hidden className="relative mt-2 flex items-center justify-center">
        <span
          className={cn(
            'absolute inline-flex h-16 w-16 rounded-full opacity-90 blur-md',
            tone.halo,
          )}
        />
        <span
          className={cn(
            'relative inline-flex h-12 w-12 items-center justify-center rounded-xl',
            tone.iconBg,
            tone.iconText,
          )}
        >
          <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
        </span>
      </span>

      <div className="relative flex flex-col gap-1.5">
        <h3 className="font-display text-title-lg text-accent-ink leading-tight font-medium">
          {resolvedTitle}
        </h3>
        {primaryDescription ? (
          <p className="text-body-sm mx-auto max-w-xs leading-relaxed text-neutral-500">
            {primaryDescription}
          </p>
        ) : null}
      </div>

      {/* Action row — primary retry + optional secondary. Forbidden states
          intentionally omit retry: there's nothing to retry until access
          changes upstream. */}
      {!forbidden && (onRetry || secondaryAction) ? (
        <div className="relative flex flex-wrap items-center justify-center gap-1.5">
          {onRetry ? (
            <Button variant="primary" size="sm" onClick={onRetry}>
              <RotateCw className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden /> Try again
            </Button>
          ) : null}
          {secondaryAction ? (
            <Button variant="ghost" size="sm" onClick={secondaryAction.onClick}>
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
              {secondaryAction.label}
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* Technical detail — collapsed by default. Only rendered when we
          recognised the description as backend noise; otherwise the raw
          message is the primary description and there's nothing to hide.
          Inline summary keeps the closed state visually small. */}
      {technicalDetail ? (
        <details className="text-meta-sm relative -mt-1 max-w-xs text-neutral-400 [&[open]>summary]:text-neutral-600 [&[open]>summary>svg]:rotate-90">
          <summary className="hover:text-accent-ink inline-flex cursor-pointer list-none items-center gap-1 font-medium transition-colors select-none">
            <ChevronRight aria-hidden className="h-3 w-3 transition-transform" strokeWidth={2} />
            Technical details
          </summary>
          <p className="mt-2 rounded-md border border-dashed border-(--color-border) px-3 py-2 text-left font-mono break-words text-neutral-500">
            {technicalDetail}
          </p>
        </details>
      ) : null}
    </div>
  );
}
